import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'
import { ApiError } from './api/base-client'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export interface ErrorHandlingOptions<T> {
  on404?: 'ignore' | 'rethrow' | { fallback: T }
  onApiError?: 'rethrow' | 'log-and-rethrow' | 'log-and-return-null'
  onOtherError?: 'rethrow' | 'log-and-rethrow' | 'log-and-return-null'
  errorContext?: string
}

export async function withErrorHandling<T>(
  operation: () => Promise<T>,
  options: ErrorHandlingOptions<T> = {},
): Promise<T | null> {
  const {
    on404 = 'rethrow',
    onApiError = 'rethrow',
    onOtherError = 'rethrow',
    errorContext,
  } = options

  try {
    return await operation()
  } catch (error) {
    const logError = (msg: string) => {
      if (errorContext) {
        console.error(`${errorContext}: ${msg}`, error)
      }
    }

    if (error instanceof ApiError && error.status === 404) {
      if (on404 === 'ignore') {
        return null
      }
      if (typeof on404 === 'object' && 'fallback' in on404) {
        return on404.fallback
      }
      if (on404 === 'rethrow') {
        throw error
      }
    }

    if (error instanceof ApiError) {
      if (onApiError === 'log-and-rethrow') {
        logError('API Error')
        throw error
      }
      if (onApiError === 'log-and-return-null') {
        logError('API Error')
        return null
      }
      if (onApiError === 'rethrow') {
        throw error
      }
    }

    if (onOtherError === 'log-and-rethrow') {
      logError('Unexpected Error')
      throw error
    }
    if (onOtherError === 'log-and-return-null') {
      logError('Unexpected Error')
      return null
    }
    if (onOtherError === 'rethrow') {
      throw error
    }

    return null
  }
}
